/**
 * Dancer activation event catalog.
 *
 * The single source of truth for dancer-lifecycle PostHog event names. Before
 * this module, events were captured with ad-hoc string literals and
 * inconsistent naming (snake_case vs kebab-case); new dancer events reference
 * these constants instead. All names are `snake_case` (the PostHog majority).
 *
 * Capture is CLIENT-SIDE ONLY — `@kit/analytics/server` is a no-op, so a
 * server-originating event must be fired from a client surface after
 * `identify(auth.users.id)` has run:
 *
 *   import { analytics } from '@kit/analytics';
 *   import { DancerEvent } from '~/lib/analytics/dancer-events';
 *   void analytics.trackEvent(DancerEvent.AuditionApplied, { audition_id });
 *
 * Property values must be `string | string[]` — stringify numbers (`String(x)`).
 *
 * These events power the activation analysis (H1–H4) in
 * `docs/runbooks/growth/dancer-activation-analysis.md`. "Being found" signals
 * (profile views / inquiries RECEIVED) are NOT here — the actor is the viewer,
 * not the dancer, so they are read from Supabase source tables instead.
 */
export const DancerEvent = {
  /** Dancer picked the `dancer` role / created a dancer profile. */
  Signup: 'dancer_signup',
  /** Dancer uploaded profile media (photo / reel). */
  MediaUploaded: 'dancer_media_uploaded',
  /** Dancer opened an audition detail page. */
  AuditionViewed: 'dancer_audition_viewed',
  /** Dancer submitted an audition application. */
  AuditionApplied: 'dancer_audition_applied',
  /** Dancer clicked to export their resume as PDF. */
  ResumeExportClicked: 'dancer_resume_export_clicked',
  /** Resume PDF export was blocked by the premium paywall (402). */
  ResumePaywallHit: 'dancer_resume_paywall_hit',
  /** Resume PDF export succeeded. */
  ResumeExportSucceeded: 'dancer_resume_export_succeeded',
  /** Dancer followed an organization. */
  OrgFollowed: 'dancer_org_followed',
} as const;
